import Box from "@mui/material/Box";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { Button, TextInput, useGetOne } from "react-admin";
import { DateTime } from "luxon";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { DrivingLesson } from "../../model/autovioCalendarEvents";
import { Student } from "../../providers/studentsProvider";
import { DrivingLessonDurationInput } from "../../inputs/DrivingLessonDurationInput";
import { DateTimeInput } from "../../inputs/DateTimeInput";
import { DrivingLicenseClassInput } from "../../inputs/DrivingLicenseClassInput";
import { DrivingLicenseClass } from "../../model/DrivingLicenseClass";
import { Column } from "../../misc/Column";
import { Row } from "../../misc/Row";
import { Form2Theme } from "../../misc/Form2";
import { SaveButton2 } from "../../buttons/SaveButton2";
import { editSimulatorDrivingLesson } from "../../api/backoffice.api";
import { SIMULATOR_DRIVING_LESSON_DURATION_OPTIONS } from "../AddAppointmentDialog/CreateSimulatorDrivingLessonForm";

interface EditSimulatorDrivingLessonFormValues {
  student: Student;
  dateTime: DateTime;
  durationInMinutes: number;
  drivingLicenseClass: "" | DrivingLicenseClass;
  remarks: string;
}

interface EditSimulatorDrivingLessonParams {
  dateTime: DateTime;
  duration: { minutes: number };
  bookedTrainingId: string;
  remarks?: string;
}

export function EditSimulatorDrivingLessonForm(props: { event: DrivingLesson; closeEditMode: () => void }) {
  const { event, closeEditMode } = props;
  const studentId = event.student.uid;
  const { data: student } = useGetOne<Student>("students", { id: studentId }, { enabled: !!studentId });
  if (!student) {
    return null;
  }
  return <_EditSimulatorDrivingLessonForm event={event} student={student} closeEditMode={closeEditMode} />;
}

function _EditSimulatorDrivingLessonForm(props: { event: DrivingLesson; student: Student; closeEditMode: () => void }) {
  const { event, student, closeEditMode } = props;
  const bookedTraining = event.student.bookedTrainingId
    ? student.bookedTrainingsById[event.student.bookedTrainingId]
    : undefined;
  const defaultValues = {
    student,
    dateTime: event.start,
    durationInMinutes: Math.round(event.end.diff(event.start).as("minutes")),
    drivingLicenseClass: bookedTraining?.drivingLicenseClass ?? "",
    remarks: event.remarks ?? "",
  } satisfies EditSimulatorDrivingLessonFormValues;
  const formProps = useForm<EditSimulatorDrivingLessonFormValues>({ defaultValues });
  return (
    <Box sx={{ minWidth: "450px" }} data-testid="EditSimulatorDrivingLessonForm">
      <FormProvider {...formProps}>
        <Form2Theme>
          <__EditSimulatorDrivingLessonForm eventId={event.id} closeEditMode={closeEditMode} />
        </Form2Theme>
      </FormProvider>
    </Box>
  );
}

function __EditSimulatorDrivingLessonForm(props: { eventId: string; closeEditMode: () => void }) {
  const { eventId, closeEditMode } = props;
  const { handleSubmit, watch } = useFormContext<EditSimulatorDrivingLessonFormValues>();
  const { student, dateTime } = watch();

  function validate(formValues: EditSimulatorDrivingLessonFormValues): EditSimulatorDrivingLessonParams {
    const { dateTime, durationInMinutes, drivingLicenseClass, remarks } = formValues;
    if (!dateTime || !dateTime.isValid) {
      throw new Error(`Invalid dateTime: ${dateTime}`);
    }
    if (!durationInMinutes) {
      throw new Error(`Invalid durationInMinutes: ${durationInMinutes}`);
    }
    if (!drivingLicenseClass) {
      throw new Error(`Invalid drivingLicenseClass: ${drivingLicenseClass}`);
    }
    const bookedTraining = student.bookedTrainings.find((it) => it.drivingLicenseClass === drivingLicenseClass);
    if (!bookedTraining) {
      throw new Error(`Student ${student.id} has no booked training for driving license class ${drivingLicenseClass}`);
    }
    return {
      dateTime,
      duration: { minutes: durationInMinutes },
      bookedTrainingId: bookedTraining.id,
      ...(remarks ? { remarks } : {}),
    } satisfies EditSimulatorDrivingLessonParams;
  }

  const onSubmit = async (formValues: EditSimulatorDrivingLessonFormValues) =>
    runSafely(
      async () => {
        const { dateTime, duration, bookedTrainingId, remarks } = validate(formValues);
        await editSimulatorDrivingLesson({
          eventId,
          start: dateTime,
          end: dateTime.plus(duration),
          bookedTrainingId,
          remarks,
        });
      },
      { onSuccess: closeEditMode },
    );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Column sx={{ gap: "20px" }}>
        <DateTimeInput source="dateTime" sx={{ mt: "5px" }} allowPastDates size="small" />
        <DrivingLessonDurationInput
          source="durationInMinutes"
          options={SIMULATOR_DRIVING_LESSON_DURATION_OPTIONS}
          size="small"
        />
        <DrivingLicenseClassInput
          student={student}
          source="drivingLicenseClass"
          label="Ausbildung für Führerscheinklasse"
          includesSimulatorDrivingLessons
          showFinishedBookedTrainings
          size="small"
        />
        <TextInput source="remarks" label="Hinweis für Fahrschüler" multiline rows={2} />
      </Column>
      <Row sx={{ justifyContent: "space-between", mt: "40px" }}>
        <Button
          startIcon={<NavigateBeforeIcon />}
          label="Zurück"
          variant="outlined"
          size="medium"
          onClick={closeEditMode}
        />
        <SaveButton2 label={dateTime && dateTime < DateTime.now() ? "Termin nachtragen" : "Speichern"} />
      </Row>
    </form>
  );
}
